import { gql } from "@apollo/client";
import { Classes } from "@blueprintjs/core";
import { toast } from "sonner";

import { Box, Flex } from "@/components/layout/flexbox";
import { ToggleField } from "@/components/pieces/form/ToggleField";
import { SUBTEXT_COLOR } from "@/css/constants";
import {
  ProductDetailQuery,
  useToggleRequireBookingRequestMutation,
} from "@/graphql";

type CoachProduct = ProductDetailQuery["productById"]["coachProduct"];

export const TOGGLE_REQUIRE_BOOKING_REQUEST = gql`
  mutation ToggleRequireBookingRequest($coachProductId: ID!) {
    toggleRequireBookingRequest(coachProductId: $coachProductId) {
      ok
    }
  }
`;

export function BookingRequestToggle({
  coachProduct,
}: {
  coachProduct: CoachProduct;
}) {
  const [toggleRequireBookingRequest, { loading }] =
    useToggleRequireBookingRequestMutation();

  const handleToggleRequiredBookingRequest = async () => {
    try {
      await toggleRequireBookingRequest({
        variables: {
          coachProductId: coachProduct.id,
        },
        optimisticResponse: {
          toggleRequireBookingRequest: {
            ok: true,
          },
        },
        update: (cache) => {
          cache.modify({
            id: cache.identify(coachProduct),
            fields: {
              coachRequireBookingRequest: () =>
                !coachProduct.requireBookingRequest,
            },
          });
        },
      });

      toast.success("Saved");
    } catch {
      toast.error("An error occurred. Please try again.");
    }
  };

  return (
    <>
      <Flex justifyContent="space-between" alignItems="center" pt={4}>
        <Box flexGrow={1}>
          <Box fontSize={20} fontWeight={600}>
            Require clients to submit a booking request
          </Box>
          <Box
            fontSize={16}
            className={loading ? Classes.SKELETON : ""}
            color={SUBTEXT_COLOR}
          >
            When this is enabled, bookings will not be automatically accepted.
            Clients will choose from your available times and you will be
            required to approve the booking.
          </Box>
        </Box>
        <Flex justifyContent="right" flexBasis={60} flexShrink={0}>
          {loading ? (
            <Box className={Classes.SKELETON} height={26} width={46} />
          ) : (
            <ToggleField
              checked={coachProduct.requireBookingRequest ?? false}
              onChange={handleToggleRequiredBookingRequest}
            />
          )}
        </Flex>
      </Flex>
    </>
  );
}
